import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import { allArticles } from '@/mocks/articles';
import SeoJsonLd from '@/components/base/SeoJsonLd';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [tocVisible, setTocVisible] = useState(false);
  const articleRef = useRef<HTMLDivElement>(null);

  const article = allArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background-50">
        <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
        <p className="text-foreground-500 text-sm mb-6">The article you are looking for does not exist or has been moved.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
            &larr; Back to Home
          </Link>
          <Link
            to="/article/what-is-superbuy-spreadsheet"
            className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
          >
            Read: What Is Superbuy Spreadsheet?
          </Link>
        </div>
      </div>
    );
  }

  const SITE_URL = 'https://superbuysheet.lol';
  const articleUrl = `${SITE_URL}/article/${article.slug}`;

  const articleSchemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.excerpt,
      image: article.imageUrl,
      datePublished: article.date,
      author: {
        '@type': 'Organization',
        name: article.author,
        url: SITE_URL,
      },
      publisher: {
        '@type': 'Organization',
        name: 'SuperBuySheet',
        url: SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png',
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': articleUrl,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Guides',
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
        },
      ],
    },
  ];

  const relatedArticles = allArticles.filter((a) => a.slug !== slug).slice(0, 3);

  const scrollToSection = (index: number) => {
    const el = document.getElementById(`section-${index}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTocVisible(false);
    }
  };

  return (
    <main>
      <SeoJsonLd data={articleSchemas} />
      <Navbar />

      <article ref={articleRef} className="pt-24 md:pt-28 pb-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              Home
            </Link>
            <span>/</span>
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              Guides
            </Link>
            <span>/</span>
            <span className="text-foreground-800 font-medium truncate">{article.title}</span>
          </nav>

          <header className="mb-10">
            <span className="inline-block text-xs font-medium px-3 py-1 rounded-full bg-primary-100 text-primary-700 mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span>&middot;</span>
              <span>{article.date}</span>
              <span>&middot;</span>
              <span>{article.readTime}</span>
            </div>
          </header>

          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Mobile TOC toggle */}
          <div className="md:hidden mb-6">
            <button
              onClick={() => setTocVisible(!tocVisible)}
              className="flex items-center gap-2 w-full px-4 py-3 rounded-xl bg-background-100 text-foreground-700 text-sm font-medium cursor-pointer"
            >
              <i className="ri-list-check"></i>
              {tocVisible ? 'Hide Table of Contents' : 'Show Table of Contents'}
              <i className={`ri-arrow-down-s-line ml-auto transition-transform ${tocVisible ? 'rotate-180' : ''}`}></i>
            </button>
            {tocVisible && (
              <div className="mt-2 p-4 bg-background-100 rounded-xl border border-background-200">
                <ul className="space-y-2">
                  {article.sections.map((section, idx) => (
                    <li key={idx}>
                      <button
                        onClick={() => scrollToSection(idx)}
                        className="text-sm text-foreground-600 hover:text-primary-600 cursor-pointer text-left"
                      >
                        {idx + 1}. {section.heading}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="flex gap-10">
            {/* Desktop TOC sidebar */}
            <aside className="hidden md:block w-56 shrink-0">
              <div className="sticky top-28">
                <p className="text-xs font-bold uppercase tracking-widest text-foreground-400 mb-4">Contents</p>
                <ul className="space-y-2.5">
                  {article.sections.map((section, idx) => (
                    <li key={idx}>
                      <button
                        onClick={() => scrollToSection(idx)}
                        className="text-sm text-foreground-500 hover:text-primary-600 cursor-pointer text-left leading-snug transition-colors"
                      >
                        {section.heading}
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-6 border-t border-background-200">
                  <p className="text-xs font-bold uppercase tracking-widest text-foreground-400 mb-3">Quick Links</p>
                  <Link
                    to="/"
                    className="block text-sm text-primary-600 hover:text-primary-700 mb-2 cursor-pointer"
                  >
                    Back to Resource Center
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="block text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    Visit Superbuy Sheet
                  </a>
                </div>
              </div>
            </aside>

            {/* Article content */}
            <div className="flex-1 min-w-0">
              <div className="prose-custom">
                {article.sections.map((section, idx) => (
                  <section key={idx} id={`section-${idx}`} className="mb-12 scroll-mt-28">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      {section.heading}
                    </h2>
                    <div className="text-foreground-700 text-base leading-[1.8] space-y-4">
                      {section.content.split('\n\n').map((paragraph, pIdx) => (
                        <p key={pIdx}>{paragraph}</p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              {/* Internal link back to home */}
              <div className="mt-10 p-6 bg-primary-50 rounded-2xl border border-primary-100">
                <p className="text-foreground-700 text-sm mb-3">
                  Want to explore more Superbuy Spreadsheet resources?
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-primary-700 font-semibold text-sm hover:text-primary-800 cursor-pointer"
                >
                  <i className="ri-home-line"></i>
                  Return to Superbuy Spreadsheet Resource Center
                </Link>
              </div>

              {/* CTA */}
              <div className="mt-10 p-6 md:p-8 bg-background-100 rounded-2xl text-center">
                <p className="text-foreground-600 text-sm mb-4">
                  Ready to explore the complete Superbuy Spreadsheet?
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-300"
                >
                  Buy It Now
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Guides */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            Related <span className="italic text-primary-600">Guides</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {relatedArticles.map((item) => (
              <Link
                key={item.slug}
                to={`/article/${item.slug}`}
                className="group bg-background-50 rounded-2xl overflow-hidden cursor-pointer hover:bg-background-200/50 transition-colors duration-300"
              >
                <div className="aspect-[3/2] overflow-hidden">
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    title={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 mb-2 inline-block">
                    {item.category}
                  </span>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-foreground-600 text-sm">{item.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FooterSection />
    </main>
  );
}